import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Config } from '../utils/Config';
import { fetchExternalSource } from '../utils/ExternalStatusAPI';

const HEALTH_STYLES = {
  OK: { pill: 'bg-secondary text-secondary-dark', dot: 'bg-secondary-dark' },
  WARNING: { pill: 'bg-primary/30 text-yellow-700', dot: 'bg-primary-dark' },
  ERROR: { pill: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
  MAINTENANCE: { pill: 'bg-gray-100 text-neutral', dot: 'bg-neutral' },
};

const STATUS_I18N_KEY = {
  OK: 'external.operational',
  WARNING: 'external.degraded',
  ERROR: 'external.outage',
  MAINTENANCE: 'external.maintenance',
};

function HealthPill({ status, label }) {
  const style = HEALTH_STYLES[status] || HEALTH_STYLES.MAINTENANCE;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {label}
    </span>
  );
}

function ExternalSourceRow({ source }) {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchExternalSource(source)
      .then(r => { if (!cancelled) setResult(r); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [source]);

  if (!result && !error) {
    return (
      <div className="flex items-center justify-between px-5 py-3.5 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-1/4" />
        <div className="h-6 bg-gray-100 rounded-full w-24" />
      </div>
    );
  }

  const status = error ? 'ERROR' : result.status;
  const label = error
    ? t('external.unavailable')
    : t(STATUS_I18N_KEY[status] || 'external.operational');

  return (
    <div className="flex items-center justify-between px-5 py-3.5">
      <span className="font-medium text-sm sm:text-base truncate mr-4">
        {source.name}
      </span>
      <HealthPill status={status} label={label} />
    </div>
  );
}

export default function ExternalStatusSection() {
  const { t } = useTranslation();
  const sources = Config.externalSources || [];

  if (sources.length === 0) return null;

  return (
    <div className="mb-10">
      <h2 className="text-sm font-semibold text-neutral uppercase tracking-wider mb-3">
        {t('external.title')}
      </h2>
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
        {sources.map((source, i) => (
          <ExternalSourceRow key={i} source={source} />
        ))}
      </div>
    </div>
  );
}
